import {
  type CanvasElementFrom,
  type IoConfig,
  type PartialPropsOf,
  type PropsOf,
  type WidthSettings,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import { type ElementSizingMode, type PropertiesConfig } from '@blaze-shared/elements-domain';
import { COLUMN_ID_OR_NAME, DEFAULT_SCHEMA } from '@blaze-shared/schema-domain';

import { registerElementDefinition } from '../../services/element-definitions.service';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { SchedulerDefinitionMapper } from './ai/scheduler.definition-mapper';
import { SchedulerElementHandler } from './ai/scheduler.element-handler';
import { schedulerV2Migration } from './migrations/002-scheduler.migration';
import { DaySelectorMode, DefaultDayMode, SchedulerComponent } from './scheduler.component';
import { type SchedulerPropsModel } from './scheduler.models';
import { SchedulerPropsBuilder } from './scheduler.props-builder';
import { SchedulerPropsExtractor } from './scheduler.props-extractor';

export type SchedulerElement = CanvasElementFrom<'scheduler'>;
export type SchedulerDef = WithProps<SchedulerPropsModel> & WithIoConfig;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    scheduler: SchedulerDef;
  }
}

const DEFAULT_WIDTH = 320;
const MIN_SIZE = 480;
const MAX_WIDTH = 900;
const WIDTH_OPTIONS: ElementSizingMode[] = ['fixed', 'full'];
const CALENDAR_INITIAL_MIN_WIDTH = 590;

registerElementDefinition({
  type: 'scheduler',
  componentType: SchedulerComponent,
  isListed: true,
  name: 'Scheduler',
  width: { mode: 'value', value: DEFAULT_WIDTH, fixed: true },
  height: { mode: 'value', value: MIN_SIZE, fixed: true },
  ioConfig: getIoConfig(),
  propsConfig: getPropsConfig(),
  propsBuilder: SchedulerPropsBuilder,
  propsExtractor: SchedulerPropsExtractor,
  defaults: getDefaults(),
  initial: getInitial(),
  groups: ['custom'],
  styleIncludePaths: ['style', 'content.title.align', 'content.title.iconPosition'],
  styleExcludePaths: ['style.dateSelector.currentState'],
  features: {
    loadingIndication: 'fadeWithIndicator',
    visualStyle: 'block',
  },
  ai: {
    name: 'scheduler',
    handler: () => SchedulerElementHandler,
    definitionMapper: () => SchedulerDefinitionMapper,
  },
  version: 2,
  migrations: {
    2: schedulerV2Migration,
  },
});

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'value',
        isDefaultDataSourceInput: true,
        isDefaultDataValueInput: true,
        bindingDefs: [],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [
      {
        eventType: 'change',
        schema: {
          columns: [
            {
              columnType: 'Date',
              dataType: 'Date',
              isProfilePicture: false,
              name: 'from',
            },
            {
              columnType: 'Date',
              dataType: 'Date',
              isProfilePicture: false,
              name: 'to',
            },
          ],
          id: '',
          name: '',
        },
      },
      { eventType: 'action', schema: DEFAULT_SCHEMA },
    ],
  };
}

function getInitial(): PartialPropsOf<'scheduler'> {
  return {
    style: {
      hours: {
        fill: { color1: '$t.fillInternal', selectorType: 'solid' },
      },
      days: {
        fill: { color1: '$t.fillInternal', selectorType: 'solid' },
      },
    },
  };
}

function getDefaults(): PropsOf<'scheduler'> {
  return {
    content: {
      title: {
        label: 'Scheduler',
        align: 'left',
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
      input: {
        daysShown: 1,
        emptyListLabel: 'No appointments available',
        maxForwardDays: '10',
        daySelector: DaySelectorMode.DEFAULT,
        defaultDay: DefaultDayMode.TODAY,
      },
    },
    style: {
      fontSize: '$t.fontSize',
      fontFamily: '$t.fontFamily',
      lineHeight: '$t.lineHeight',
      general: {
        title: { color1: '$t.title', selectorType: 'solid' },
        accent: { color1: '$t.accent', selectorType: 'solid' },
        offset: { margin: '$t.spacingExternal', padding: '$t.spacingInternal' },
      },
      titleBlock: {
        fontStyle: '$t.headerFontStyle' as any,
        fontSize: '$t.headerFontSize',
        fontFamily: '$t.headerFontFamily',
        fontWeight: '$t.headerFontWeight',
        iconSize: '$t.headerIconSize',
      },
      days: {
        text: { color1: '$t.value', selectorType: 'solid' },
        fill: { color1: '$t.fill', selectorType: 'solid' },
        outline: { color1: '$t.outline', selectorType: 'none' },
        outlineWidth: '1px',
        radius: '24px',
      },
      hours: {
        text: { color1: '$t.value', selectorType: 'solid' },
        fill: { color1: '$t.fill', selectorType: 'none' },
        outline: { color1: '$t.outline', selectorType: 'solid' },
        outlineWidth: '1px',
        radius: '$t.radius',
      },

      dateSelector: {
        currentState: 'default',
        default: {
          day: { color1: '$t.value', selectorType: 'solid' },
          fill: { color1: '$t.canvas', selectorType: 'solid' },
          outline: { color1: '$t.canvas', selectorType: 'none' },
          outlineWidth: '1px',
          radius: '16px',
        },
        hover: {
          day: { color1: '$t.value', selectorType: 'solid' },
          fill: { color1: '$t.canvas', selectorType: 'solid' },
          outline: { color1: '$t.accent', selectorType: 'solid' },
          outlineWidth: '1px',
          radius: '16px',
        },
        selected: {
          day: { color1: '$t.fill', selectorType: 'solid' },
          fill: { color1: '$t.accent', selectorType: 'solid' },
          outline: { color1: '$t.canvas', selectorType: 'none' },
          outlineWidth: '1px',
          radius: '16px',
        },
        dayWithEvents: { color1: '$t.value_a15', selectorType: 'solid' },
      },

      background: {
        fill: { color1: '$t.fillExternal', selectorType: 'solid' },
        outlineWidth: '$t.containerOutlineWidth',
        outline: { color1: '$t.outline', selectorType: '$t.containerOutlineType' as any },
        radius: '$t.radius',
        visualEffect: {
          type: '$t.effectType',
          data: {
            x: '$t.effectX',
            y: '$t.effectY',
            blur: '$t.effectBlur',
            color1: '$t.effectColor1',
          },
        },
      },
    },
  };
}

function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('scheduler', [
    epc.section('content', 'Content', [
      epc.subsections.dataSubsection(
        'data.?key:value.enabled',
        'Data',
        [],
        [
          epc.fields.selectDataSourceField(
            { key: 'value', label: 'Source', sources: 'list', bindings: [] },
            'normal',
            'Source',
            { hideAiButton: true },
          ),
          epc.utils.conditionalField(
            epc.fields.selectFieldFromSchema(
              'content.input.startTimeColumn',
              '',
              'data.?key:value.dataSource',
              {
                placeholder: 'Select starting time column',
                valueField: COLUMN_ID_OR_NAME,
                layoutMode: 'datasource-subselection',
                hideLabel: true,
                optionsFilter: epc.utils.columnFormatFilter('!secureText'),
              },
            ),
            'data.?key:value.dataSource',
            (value) => !value,
          ),
        ],
        { enabledByDefault: true },
      ),

      epc.subsections.subsection('content.input', 'Options', [
        epc.utils.conditionalField(
          epc.fields.selectFieldFromList(
            'content.input.daysShown',
            'Days shown',
            [1, 3, 5, 7].map((value) => ({ label: value.toString(), value })),
            { allowNone: false, showSelection: true },
          ),
          'content.input.daySelector',
          (value) => value === DaySelectorMode.CALENDAR,
        ),
        epc.fields.textInput('content.input.emptyListLabel', 'Empty label'),
        epc.utils.conditionalField(
          epc.fields.textInput('content.input.maxForwardDays', 'Days forward', {
            inputType: 'number',
          }),
          'content.input.daySelector',
          (value) => value === DaySelectorMode.CALENDAR,
        ),
        epc.utils.observedField(
          epc.fields.selectFieldFromList(
            'content.input.daySelector',
            'Day selector',
            [
              { value: DaySelectorMode.DEFAULT, label: 'Week' },
              { value: DaySelectorMode.CALENDAR, label: 'Month' },
            ],
            { allowNone: false, showSelection: true },
          ),
          (
            model: SchedulerPropsModel & { root?: { width?: WidthSettings } },
            mode: DaySelectorMode,
          ) => {
            let updates: Record<string, any> = {};

            const width = model.root?.width;
            if (
              mode === DaySelectorMode.CALENDAR &&
              (!width?.mode || width.mode === 'value') &&
              (width?.value ?? 0) < CALENDAR_INITIAL_MIN_WIDTH
            ) {
              updates = {
                'root.width': {
                  value: CALENDAR_INITIAL_MIN_WIDTH,
                  fixed: true,
                  mode: 'value',
                },
              };
            }

            updates['style.days.radius'] = mode === DaySelectorMode.CALENDAR ? '16px' : '24px';

            return updates;
          },
        ),
        epc.fields.selectFieldFromList(
          'content.input.defaultDay',
          'Default day',
          [
            { value: DefaultDayMode.TODAY, label: 'Today' },
            { value: DefaultDayMode.FIRST_AVAILABLE, label: 'First available' },
          ],
          { allowNone: false, showSelection: true },
        ),
      ]),

      epc.subsections.visibilitySubsection(),

      epc.subsections.subsection(
        'content',
        'Action',
        [epc.fields.buttonActionField('content.action')],
        { hideLabel: true },
      ),

      epc.subsections.subsection('content.container', 'Size', [
        epc.root.elementWidthField(
          { min: MIN_SIZE, max: MAX_WIDTH, unit: 'px', step: 1 },
          WIDTH_OPTIONS,
        ),
      ]),
      epc.subsections.titleContentSubsection('content'),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),
        epc.subsections.subsection('style.general', 'Padding & Margin', [
          epc.fields.spacingAndAlignField('style.general.offset', {
            hasMargin: true,
            hasPadding: true,
          }),
        ]),
        epc.subsections.subsection('style.general', 'General', [
          epc.fields.colorField('style.general.accent', 'Accent', {
            allowGradient: false,
            allowNone: false,
          }),
        ]),
        epc.subsections.elementUIPartStyleSubsection('style.days', 'Days', {
          hideWhen: (_, __, props: SchedulerPropsModel) => {
            return props.content.input.daySelector === 'CALENDAR';
          },
        }),
        epc.subsections.elementUIPartStyleSubsection('style.hours', 'Hours', {
          hideWhen: (_, __, props: SchedulerPropsModel) => {
            return props.content.input.daySelector === 'CALENDAR';
          },
        }),
        epc.subsections.calendarDaysStyleSubsection('style.dateSelector', 'Date Selector', [], {
          hideWhen: (_, __, props: SchedulerPropsModel) => {
            return props.content.input.daySelector === DaySelectorMode.DEFAULT;
          },
        }),

        epc.subsections.elementBackgroundSubsection('style.background', 'Background', {
          hasOutlineSides: true,
        }),
        epc.subsections.titleStyleSubsection({
          enabled: 'content.title.enabled',
          color: 'style.general.title',
          fontFamily: 'style.titleBlock.fontFamily',
          fontWeight: 'style.titleBlock.fontWeight',
          fontStyle: 'style.titleBlock.fontStyle',
          fontSize: 'style.titleBlock.fontSize',
          horizontalAlign: 'content.title.align',
          icon: 'content.title.icon',
          iconColor: 'content.title.iconColor',
          iconPosition: 'content.title.iconPosition',
          iconSize: 'style.titleBlock.iconSize',
        }),
      ],
      true,
    ),
  ]);
}
